import React, { useEffect, useMemo } from 'react';
import Layout from '../Components/Layout';
import { 
  Typography, 
  Box,
  Card,
  CardContent,
  Button
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import useCallOnce from '../hooks/useCallOnce';
import { checkShopifyConnection } from '../redux/actions/shopifyActions';
import { getWidgetColor } from '../redux/actions/widgetActions';
import { WIDGET_URL } from '../Config';

function TestZone() {
  const dispatch = useDispatch();
  const { isConnected, stores, raw } = useSelector((s) => s.shopify);
  const { widgetColor: reduxWidgetColor } = useSelector((s) => s.widget);
  // Kick off required data loads once
  useCallOnce('shopify/getUserDataWithStores', () => {
    dispatch(checkShopifyConnection());
  }, [dispatch]);

   // Load widget color on component mount using useCallOnce
   useCallOnce('ecommerce/getWidgetColor', () => {
    dispatch(getWidgetColor());
  });

  // Derive parameters from state
  const agentId = raw?.data?.user?.agentId || '';
  const domain = stores?.[0]?.storeUrl || '';
  const accessToken = raw?.data?.shopifyStores?.[0]?.accessToken || stores?.[0]?.accessToken || '';
  const widgetColor = reduxWidgetColor || '#d67900';

  // Build the script text for display
  // const scriptText = useMemo(() => (
  //   `<script src="https://dl1961.ormeagent.ai/chat-widget-bundled.js"></script>\n` +
  //   `<script>\n` +
  //   `    document.addEventListener('DOMContentLoaded', function() {\n` +
  //   `        new ChatWidget('#testzone-widget', {\n` +
  //   `            agentId: '${agentId}',\n` +
  //   `            domain: "${domain}",\n` +
  //   `            accessToken: "${accessToken}",\n` +
  //   `            color: '${widgetColor}'\n` +
  //   `        });\n` +
  //   `    });\n` +
  //   `</script>`
  // ), [agentId, domain, accessToken, widgetColor]);

  // Load widget script and initialize inside the render box
  useEffect(() => {
    if (!isConnected || !agentId || !domain || !accessToken) return undefined;

    const container = document.getElementById('testzone-widget');
    if (!container) return undefined;

    // Clear previous render to avoid duplicates
    container.innerHTML = '';

    const initializeWidget = () => {
      try {
        if (container.dataset.widgetInit === '1') return;
        if (typeof window.ChatWidget === 'function') {
          new window.ChatWidget('#testzone-widget', {
            agentId: agentId,
            domain: domain,
            accessToken: accessToken,
            themeColor: widgetColor
          });
          container.dataset.widgetInit = '1';

          // Nudge the launcher into the box's bottom-right corner
          // Give the widget a moment to render, then adjust styles
          const moveIntoCorner = () => {
            try {
              const fixedNodes = container.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]');
              fixedNodes.forEach((node) => {
                node.style.position = 'absolute';
                node.style.right = '12px';
                node.style.bottom = '12px';
                node.style.left = 'auto';
                node.style.top = 'auto';
              });
            } catch (_) {}
          };
          setTimeout(moveIntoCorner, 50);
          setTimeout(moveIntoCorner, 250);
          setTimeout(moveIntoCorner, 1000);

          // Force styles inside the widget's shadow root
          const applyShadowCss = () => {
            try {
              // Try common host id first
              let host = container.querySelector('#chat-widget-root');
              if (!host) {
                // Fallback: find any element with a shadowRoot under our container
                const candidates = Array.from(container.querySelectorAll('*')).filter((el) => el.shadowRoot);
                host = candidates[0];
              }
              const shadow = host && host.shadowRoot ? host.shadowRoot : null;
              if (!shadow) return false;
              // Inject a <style> into the shadow DOM to override positioning
              const style = document.createElement('style');
              style.textContent = `
                .chat-widget-container { 
                  position: static; 
                  
                }
                .chat-widget-call-btn {
                  bottom: 0px;
                }
              `;
              shadow.appendChild(style);
              return true;
            } catch (_) { return false; }
          };
          // Retry a few times until the shadow root is ready
          let attempts = 0;
          const retry = () => {
            attempts += 1;
            if (applyShadowCss()) return;
            if (attempts < 10) setTimeout(retry, 100);
          };
          setTimeout(retry, 100);
        }
      } catch (e) {
        console.error('ChatWidget init error:', e);
      }
    };

    if (typeof window.ChatWidget === 'function') {
      initializeWidget();
      return () => { delete container.dataset.widgetInit; };
    }

    let script = document.querySelector('script[data-chat-widget-lib="1"]');
    const onLoad = () => initializeWidget();

    if (!script) {
      script = document.createElement('script');
      script.src = `${WIDGET_URL}/chat-widget-bundled.js`;
      script.async = true;
      script.setAttribute('data-chat-widget-lib', '1');
      script.addEventListener('load', onLoad);
      document.body.appendChild(script);
    } else {
      script.addEventListener('load', onLoad);
    }

    return () => {
      try {
        if (script) script.removeEventListener('load', onLoad);
        // Best-effort teardown/reset
        delete container.dataset.widgetInit;
        // If library exposes a destroy API, invoke it
        try {
          if (window && typeof window.ChatWidgetDestroy === 'function') {
            window.ChatWidgetDestroy('#testzone-widget');
          }
        } catch (_) {}
        // Remove any shadow-host and injected styles under our container
        try {
          const host = container.querySelector('#chat-widget-root');
          if (host && host.shadowRoot) {
            Array.from(host.shadowRoot.querySelectorAll('style')).forEach((s) => {
              try { s.parentNode && s.parentNode.removeChild(s); } catch (_) {}
            });
          }
          if (host && host.parentNode) host.parentNode.removeChild(host);
        } catch (_) {}
        // Clear container content
        container.innerHTML = '';
      } catch (_) {}
    };
  }, [isConnected, agentId, domain, accessToken, widgetColor]);

  return (
    <Layout>
      <Box sx={{ mt: 3, px: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Test Zone
        </Typography>

        <Card elevation={3} sx={{ borderRadius: 2 }}>
          <CardContent>
            {/* <Typography 
              variant="body1" 
              sx={{ 
                color: '#6b7280', 
                fontSize: '16px',
                fontFamily: FONT_FAMILY
              }}
            >
              Test Zone - Experiment with new features and functionality.
            </Typography> */}
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 1 }}>
              Shopify connection: 
              {/* {isConnected ? 'Connected' : 'Not connected'} */}
              <Button
                variant="outlined"
                disableRipple
                sx={{
                  borderColor: isConnected ? 'success.main' : 'error.main',
                  color: isConnected ? 'success.main' : 'error.main',
                  fontWeight: 700,
                  px: 2.5,
                  pointerEvents: 'none',
                  ml: 1,
                }}
              >
                {isConnected ? 'CONNECTED' : 'NOT CONNECTED'}
              </Button>
            </Typography>
            {/* Render widget preview only when required data exists to avoid runtime errors */}
            {isConnected && agentId && domain && accessToken && (
            <Box sx={{ mt: 2, p: 2, border: '1px dashed #d1d5db', borderRadius: 1, position: 'relative', minHeight: 500 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Widget Preview
              </Typography>
              <Box id="testzone-widget" sx={{ position: 'absolute', inset: 0 }} />
            </Box>
             )}
            {/* {!isConnected && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed #fecaca', background: '#fff7ed', color: '#b91c1c', borderRadius: 1 }}>
                Unable to connect to Shopify right now. Please try again later.
              </Box>
            )} */}

            {/* {isConnected && agentId && domain && accessToken && (
              <Box sx={{ mt: 2, background: '#f5f5f5', border: '1px solid #e5e7eb', borderRadius: 1, p: 2, fontFamily: 'monospace', fontSize: '12px', overflow: 'auto' }}>
                <Box component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {scriptText}
                </Box>
              </Box>
            )} */}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
}

export default TestZone;
